// ============================================
// Event Bus Helpers
//
// Generic severity calculation, title/summary generation,
// and watch-type resolution. Derives all metadata from
// EVENT_CATALOG (single source of truth) rather than
// hardcoded arrays.
//
// Exports HEMOLOGIX_EVENT_CONTRACT — the typed contract
// implementation for the Hemologix building. Other buildings
// (e.g. Medek RPM) implement BuildingEventContract with
// their own event types, watch aliases, and summaries.
//
// USE CASES:
//   UC-EH-1: Promise Keeper external watcher fires "fda_rule" →
//            mapWatchToEventType resolves to FDA_RULE_CHANGE →
//            calculateSeverity reads catalog base + payload escalation →
//            generateEventTitle/Summary produce human-readable text.
//   UC-EH-2: Health check detects failure → publishOpsEvent("HEALTH_CHECK_FAILED")
//            → watch alias "health_check" resolves for dashboard routing.
//   UC-EH-3: Admin dashboard calls generateEventSummary to render
//            event cards without accessing raw payloads.
//   UC-EH-4: New building swaps HEMOLOGIX_EVENT_CONTRACT for its own
//            contract — all helper functions work unchanged.
// ============================================

import type { PlatformEventType, PlatformEvent } from '../../types/eventBus';
import type {
  BuildingEventContract, SummaryTemplate, EventSeverity,
} from '../../types/eventContract';
import type { HemologixEventType, EventDomain } from '../../types/eventCatalog';
import { EVENT_CATALOG } from '../../types/eventCatalog';

// ── Watch type → Event type mapping (Hemologix-specific) ──

const HEMOLOGIX_WATCH_TYPE_MAP: Record<string, PlatformEventType> = {
  // Regulatory watch types (Promise Keeper)
  'fda_rule': 'FDA_RULE_CHANGE',
  'fda_rule_change': 'FDA_RULE_CHANGE',
  'fda_enforcement': 'FDA_ENFORCEMENT_ACTION',
  'fda_enforcement_action': 'FDA_ENFORCEMENT_ACTION',
  'fda_ingredient': 'FDA_INGREDIENT_ACTION',
  'fda_ingredient_action': 'FDA_INGREDIENT_ACTION',
  'state_law': 'STATE_LAW_CHANGE',
  'state_law_change': 'STATE_LAW_CHANGE',
  'dea_telehealth': 'DEA_TELEHEALTH_RULE',
  'dea_telehealth_rule': 'DEA_TELEHEALTH_RULE',
  'cms_reimbursement': 'CMS_REIMBURSEMENT_CHANGE',
  'cms_reimbursement_change': 'CMS_REIMBURSEMENT_CHANGE',
  'hhs_policy': 'HHS_POLICY_SHIFT',
  'hhs_policy_shift': 'HHS_POLICY_SHIFT',
  'posture_reversal': 'POSTURE_REVERSAL',
  // Clinical watch types (Master Formulator)
  'interaction': 'INTERACTION_DISCOVERED',
  'interaction_discovered': 'INTERACTION_DISCOVERED',
  'study_retracted': 'STUDY_RETRACTED',
  'formulary_drift': 'FORMULARY_DRIFT_DETECTED',
  'formulary_drift_detected': 'FORMULARY_DRIFT_DETECTED',
  'md_expiry': 'MEDICAL_DIRECTOR_EXPIRY',
  'medical_director_expiry': 'MEDICAL_DIRECTOR_EXPIRY',
  'biomarker_range': 'BIOMARKER_RANGE_REVISED',
  'biomarker_range_revised': 'BIOMARKER_RANGE_REVISED',
  'guideline_update': 'GUIDELINE_UPDATED',
  'guideline_updated': 'GUIDELINE_UPDATED',
  'xprize': 'XPRIZE_UPDATE',
  'xprize_update': 'XPRIZE_UPDATE',
  // Platform watch types
  'compliance_deadline': 'COMPLIANCE_DEADLINE',
  'doc_regen': 'DOCUMENT_REGENERATION_REQUIRED',
  'document_regeneration': 'DOCUMENT_REGENERATION_REQUIRED',
  'breach_risk': 'COMMITMENT_BREACH_RISK',
  'commitment_breach_risk': 'COMMITMENT_BREACH_RISK',
  'system_health': 'SYSTEM_HEALTH_ALERT',
  'system_health_alert': 'SYSTEM_HEALTH_ALERT',
  'debt_threshold': 'DEBT_THRESHOLD_BREACH',
  'debt_threshold_breach': 'DEBT_THRESHOLD_BREACH',
  // Ops watch types (Phase 7)
  'cost_budget': 'COST_BUDGET_BREACH',
  'cost_budget_breach': 'COST_BUDGET_BREACH',
  'dependency_vuln': 'DEPENDENCY_VULNERABILITY',
  'dependency_vulnerability': 'DEPENDENCY_VULNERABILITY',
  'remediation': 'REMEDIATION_EXECUTED',
  'remediation_executed': 'REMEDIATION_EXECUTED',
  'health_check': 'HEALTH_CHECK_FAILED',
  'health_check_failed': 'HEALTH_CHECK_FAILED',
  'tos_breach': 'TOS_COMPLIANCE_BREACH',
  'tos_compliance_breach': 'TOS_COMPLIANCE_BREACH',
};

/** Map a regulatory watch type string to its corresponding PlatformEventType. */
export function mapWatchToEventType(watchType: string): PlatformEventType | null {
  const normalized = watchType.toLowerCase().trim();
  return HEMOLOGIX_WATCH_TYPE_MAP[normalized] ?? null;
}

// ── Severity calculation (catalog-driven) ──

/** Escalate severity by one level (info → low → medium → high → critical). */
function escalateSeverity(severity: EventSeverity): EventSeverity {
  switch (severity) {
    case 'info': return 'low';
    case 'low': return 'medium';
    case 'medium': return 'high';
    case 'high': return 'critical';
    case 'critical': return 'critical';
  }
}

/**
 * Calculate severity based on event type and payload.
 * Base severity is derived from EVENT_CATALOG (single source of truth)
 * rather than hardcoded arrays.
 * Payload can escalate via:
 * - `urgency`: 'critical' | 'high' | 'medium' | 'low'
 * - `affectedCount`: number (high count escalates)
 * - `deadlineDays`: number (tight deadline escalates)
 */
export function calculateSeverity(
  eventType: PlatformEventType,
  payload: Record<string, any>,
): PlatformEvent['severity'] {
  // Read base severity from the catalog — no hardcoded severity arrays
  const entry = EVENT_CATALOG[eventType];
  const baseSeverity: EventSeverity = entry?.defaultSeverity ?? 'low';

  // Payload-based escalation
  if (payload.urgency === 'critical') return 'critical';
  if (payload.urgency === 'high' && baseSeverity !== 'critical') return 'high';

  // Tight deadline escalates by one level
  if (typeof payload.deadlineDays === 'number' && payload.deadlineDays <= 7) {
    return escalateSeverity(baseSeverity);
  }

  // High affected count escalates by one level
  if (typeof payload.affectedCount === 'number' && payload.affectedCount >= 10) {
    return escalateSeverity(baseSeverity);
  }

  return baseSeverity;
}

// ── Title generation (catalog-driven) ──

/**
 * Generate a human-readable event title from the event type and payload.
 * Uses payload.title if provided, otherwise builds from catalog label + subject.
 */
export function generateEventTitle(
  eventType: PlatformEventType,
  payload: Record<string, any>,
): string {
  // If payload already has a title, use it
  if (typeof payload.title === 'string' && payload.title.trim()) {
    return payload.title.trim();
  }

  // Read label from catalog — no hardcoded EVENT_TYPE_LABELS
  const entry = EVENT_CATALOG[eventType];
  const label = entry?.label ?? eventType;
  const subject = payload.subject || payload.name || payload.ingredient || payload.state || '';

  if (subject) {
    return `${label}: ${subject}`;
  }

  return label;
}

// ── Summary templates (Hemologix-specific) ──

const HEMOLOGIX_SUMMARY_TEMPLATES: Partial<Record<PlatformEventType, SummaryTemplate>> = {
  FDA_RULE_CHANGE: (p) => {
    const ac = typeof p.affectedCount === 'number' ? p.affectedCount : 0;
    const src = p.source || p.agency || '';
    return `FDA rule change${src ? ` from ${src}` : ''} may affect ${ac || 'platform'} product${ac !== 1 ? 's' : ''}. Review required.`;
  },
  FDA_ENFORCEMENT_ACTION: (p) =>
    `FDA enforcement action issued${p.target ? ` targeting ${p.target}` : ''}. Immediate compliance review required.`,
  FDA_INGREDIENT_ACTION: (p) =>
    `FDA action on ingredient${p.ingredient ? ` "${p.ingredient}"` : ''}. Formulary impact assessment needed.`,
  STATE_LAW_CHANGE: (p) =>
    `${p.state || 'State'} law change affecting ${p.area || 'operations'}. Legal review required.`,
  DEA_TELEHEALTH_RULE: () =>
    `DEA telehealth prescribing rule change. Prescribing protocol review needed.`,
  CMS_REIMBURSEMENT_CHANGE: () =>
    `CMS reimbursement policy change. Pricing and billing review required.`,
  HHS_POLICY_SHIFT: (p) =>
    `HHS policy shift${p.area ? ` in ${p.area}` : ''}. Compliance posture review recommended.`,
  POSTURE_REVERSAL: (p) =>
    `Regulatory posture reversal detected${p.previousPosture ? ` (was: ${p.previousPosture})` : ''}. Urgent re-evaluation required.`,
  INTERACTION_DISCOVERED: (p) =>
    `New drug interaction discovered${p.compounds ? ` involving ${p.compounds}` : ''}. Formulary and safety rules must be updated.`,
  STUDY_RETRACTED: (p) =>
    `Clinical study retracted${p.studyId ? ` (${p.studyId})` : ''}. Evidence base for affected products needs review.`,
  FORMULARY_DRIFT_DETECTED: (p) => {
    const ac = typeof p.affectedCount === 'number' ? p.affectedCount : 0;
    return `Formulary drift detected — ${ac || 'some'} product${ac !== 1 ? 's' : ''} may be out of alignment with current evidence.`;
  },
  MEDICAL_DIRECTOR_EXPIRY: (p) =>
    `Medical director license${p.state ? ` in ${p.state}` : ''} expiring${p.expiryDate ? ` on ${p.expiryDate}` : ' soon'}. Renewal action required.`,
  BIOMARKER_RANGE_REVISED: (p) =>
    `Biomarker reference range revised${p.biomarker ? ` for ${p.biomarker}` : ''}. Protocol thresholds may need updating.`,
  GUIDELINE_UPDATED: (p) =>
    `Clinical guideline updated${p.guideline ? `: ${p.guideline}` : ''}. Protocol alignment review recommended.`,
  XPRIZE_UPDATE: (p) =>
    `XPRIZE update${p.milestone ? `: ${p.milestone}` : ''}. Review for platform impact.`,
  COMPLIANCE_DEADLINE: (p) => {
    const ac = typeof p.affectedCount === 'number' ? p.affectedCount : 0;
    return `Compliance deadline${p.deadlineDays ? ` in ${p.deadlineDays} days` : ' approaching'}. ${ac ? `${ac} item${ac !== 1 ? 's' : ''} affected.` : ''}`;
  },
  DOCUMENT_REGENERATION_REQUIRED: (p) => {
    const ac = typeof p.affectedCount === 'number' ? p.affectedCount : 0;
    return `${ac || 'One or more'} document${ac !== 1 ? 's' : ''} require${ac === 1 ? 's' : ''} regeneration due to upstream changes.`;
  },
  COMMITMENT_BREACH_RISK: (p) =>
    `Commitment breach risk detected${p.commitmentId ? ` for ${p.commitmentId}` : ''}. SLA or obligation may not be met.`,
  SYSTEM_HEALTH_ALERT: (p) =>
    `System health alert${p.system ? ` for ${p.system}` : ''}. ${p.message || 'Investigation recommended.'}`,
  DEBT_THRESHOLD_BREACH: (p) =>
    `Codebase debt score ${p.debtScore ? `at ${p.debtScore}` : 'exceeded threshold'}. ${p.findingsCount ? `${p.findingsCount} open findings.` : ''} Review recommended.`,
};

/**
 * Generate a concise event summary from the event type and payload.
 * Uses payload.summary if provided, otherwise uses the building's
 * registered summary template.
 */
export function generateEventSummary(
  eventType: PlatformEventType,
  payload: Record<string, any>,
): string {
  // If payload already has a summary, use it
  if (typeof payload.summary === 'string' && payload.summary.trim()) {
    return payload.summary.trim();
  }

  const template = HEMOLOGIX_SUMMARY_TEMPLATES[eventType];
  if (template) {
    return template(payload);
  }

  return `Platform event: ${eventType}. Review required.`;
}

// ── Hemologix Event Contract ──

/** The Hemologix building's event contract implementation. */
export const HEMOLOGIX_EVENT_CONTRACT: BuildingEventContract<HemologixEventType, EventDomain> = {
  buildingId: 'hemologix',
  catalog: EVENT_CATALOG,
  watchTypeMap: HEMOLOGIX_WATCH_TYPE_MAP as Record<string, HemologixEventType>,
  summaryTemplates: HEMOLOGIX_SUMMARY_TEMPLATES as Partial<Record<HemologixEventType, SummaryTemplate>>,
};
